<script setup lang="ts">
import { FormKitLazyProvider } from '@formkit/vue'
interface PasswordData {
  'curent-password': string
  'new-password-group': {
    'new-password': string
    'new-password_confirm': string
  }
}

const emits = defineEmits<{
  (e: 'success'): void
}>()

const { updatePassword } = useCustomerPassword()
const { user, refreshUser } = useUser()

// Error handling now done through try/catch in updatePassword calls
const { pushError, pushSuccess } = useNotifications()
const { t } = useI18n()

const loadingData = ref(false)

const invokeChange = async (data: PasswordData): Promise<void> => {
  loadingData.value = true
  try {
    const changePasswordResult = await updatePassword({
      password: data['curent-password'],
      newPassword: data['new-password-group']['new-password'],
      newPasswordConfirm: data['new-password-group']['new-password_confirm'],
    })
    await refreshUser()

    if (changePasswordResult) {
      pushSuccess(t('shop.accountPage.profilePage.changePasswordPage.updatePasswordSuccessMessage'))
      emits('success')
    }
    // Success message already shown above
  } catch {
    pushError(t('shop.accountPage.profilePage.changePasswordPage.updatePasswordErrorMessage'))
  } finally {
    loadingData.value = false
  }
}
</script>
<template>
<FormKitLazyProvider config-file="true">

  <p>{{ t('shop.accountPage.profilePage.changePasswordPage.description') }}</p>
  <p v-if="user?.email">
    {{ t('shop.accountPage.profilePage.changePasswordPage.currentEmailMessage') }}
    <strong>{{ user?.email }}</strong>
  </p>
  <ShopNotifications />
  <FormKit type="form" :actions="false" :incomplete-message="false" @submit="invokeChange">
    <div class="wrapper">
      <Password
        id="change-password-current-password"
        name="curent-password"
        :required="true"
        :disabled="loadingData"
        :label="`${t('shop.accountPage.profilePage.changePasswordPage.currentPassword')} *`"
      />
      <FormKit type="group" name="new-password-group">
        <Password
          id="change-password-new-password"
          name="new-password"
          :required="true"
          :disabled="loadingData"
          validation="length:8"
          :label="`${t('shop.accountPage.profilePage.changePasswordPage.newPassword')} *`"
        />
        <Password
          id="change-password-new-password_confirm"
          name="new-password_confirm"
          :required="true"
          :disabled="loadingData"
          validation="confirm|length:8"
          :label="`${t('shop.accountPage.profilePage.changePasswordPage.confirmPassword')} *`"
        />
      </FormKit>
    </div>
    <Button primary slide type="submit" :disabled="loadingData">
      {{ t('shop.accountPage.profilePage.changePasswordPage.submit') }}
    </Button>
  </FormKit>

</FormKitLazyProvider>
</template>
<style scoped lang="scss">
.wrapper {
  display: grid;
  gap: var(--form-spacing);
  margin-bottom: var(--form-spacing);
}
</style>
